import { composeClassNames } from "@faedev/editor-utils";
import { CSSProperties, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import Content from "src/components/Content";
import Heading from "src/components/Heading";
import Paragraph from "src/components/Paragraph";
import ReactPlayer from "src/components/ReactPlayer";
import Translate from "src/components/Translate";
import constants from "src/constants";
import gutter from "src/functions/gutter";
import Play from "src/icons/Play";
import { sButton } from "src/styles/sButton.module.scss";
import { sButtons } from "src/styles/sButtons.module.scss";
import { sCta } from "src/styles/sCta.module.scss";
import { sVideoPlayer } from "src/styles/sVideoPlayer.module.scss";
import { VariantSize } from "src/types/VariantSize";

type Props = {
  backgroundColor?: string;
  button?: string;
  buttonClick?: () => void;
  buttonSizes?: "sm" | "nm" | "lg";
  className?: (string | string) | null | undefined;
  content?: JSX.Element | null;
  headingType?: React.ElementType;
  imageSrc?: string;
  mediaPosition?: "left" | "right";
  mediaMobilePosition?: "top" | "bottom";
  secondButton?: string;
  secondButtonClick?: () => void;
  sizeHeading?: VariantSize | null | undefined;
  sizeParagraph?: VariantSize | null | undefined;
  style?: CSSProperties | null | undefined;
  text?: string;
  textColor?: string;
  title: string;
  videoSrc?: string;
  videoSize?: "normal" | "wide";
};

const Cta = ({
  backgroundColor,
  button,
  buttonClick,
  buttonSizes = "lg",
  className,
  content,
  headingType,
  imageSrc,
  mediaPosition,
  mediaMobilePosition,
  secondButton,
  secondButtonClick,
  sizeHeading,
  sizeParagraph,
  style,
  title,
  text,
  textColor,
  videoSrc,
  videoSize = "normal",
}: Props) => {
  const mediaContent = !imageSrc && !videoSrc ? false : true;
  const InnerContent = (
    <div className={`cta-content`} style={style}>
      <Heading
        align="left"
        size={sizeHeading}
        style={{ color: textColor }}
        title={<Translate tKey={title} />}
        type={headingType ? headingType : "h2"}
      />
      {content ? (
        content
      ) : (
        <Paragraph size={sizeParagraph || "xn"} style={{ color: textColor }}>
          <Translate tKey={text} />
        </Paragraph>
      )}
      <div
        className={composeClassNames([sButtons, "buttons"])}
        style={gutter("margin", "top", "xl")}
      >
        {button && (
          <Button
            className={composeClassNames([sButton, className])}
            onClick={buttonClick}
            size={buttonSizes !== "nm" ? buttonSizes : null}
            variant="primary"
          >
            <Translate tKey={button} />
          </Button>
        )}
        {secondButton && (
          <Button
            variant="outline-primary"
            className={composeClassNames([sButton, className])}
            onClick={secondButtonClick}
            size={buttonSizes !== "nm" ? buttonSizes : null}
          >
            <Translate tKey={secondButton} />
          </Button>
        )}
      </div>
    </div>
  );

  const Image = (
    <img src={imageSrc} alt={title} title={title} className="cta-image" />
  );

  const [playing, setPlaying] = useState(false);
  const Video = (
    <div className="cta-video">
      <div
        style={{ width: "100%" }}
        className={composeClassNames([
          sVideoPlayer,
          className,
          "video-player-wrapper",
        ])}
      >
        {!playing && (
          <div
            className={composeClassNames(["video-player-cover"])}
            onClick={() => {
              setPlaying(true);
            }}
          >
            <div
              className={composeClassNames([
                "video-player-play",
                playing && "disable",
              ])}
              onClick={() => {
                setPlaying(true);
              }}
            >
              <Play className={"play-button"} />
            </div>
          </div>
        )}
        <ReactPlayer
          controls={true}
          width={"100%"}
          height={"100%"}
          className={"video-player"}
          url={videoSrc}
          playing={playing}
        />
      </div>
    </div>
  );

  return (
    <Content
      className={composeClassNames([
        sCta,
        mediaPosition && `media-${mediaPosition}`,
        className,
      ])}
      style={{ background: backgroundColor || constants.Theme.white }}
      fluid={`full`}
      verticalSpace={`lg`}
    >
      <div className="cta-container">
        <Row>
          {mediaContent && (
            <Col
              xs={{
                span: 12,
                order: mediaMobilePosition === `top` ? 0 : 2,
              }}
              lg={{
                span: videoSize === "wide" ? 7 : 6,
                order: 1,
              }}
              xl={{
                span: videoSize === "wide" ? 8 : 6,
                order: 1,
              }}
              className={`cta-media`}
            >
              {videoSrc ? Video : imageSrc ? Image : null}
            </Col>
          )}
          <Col
            lg={{
              span: mediaContent ? (videoSize === "wide" ? 5 : 6) : 12,
              order: mediaPosition === `right` ? 0 : 2,
            }}
            xl={{
              span: mediaContent ? (videoSize === "wide" ? 4 : 6) : 12,
              order: mediaPosition === `right` ? 0 : 2,
            }}
          >
            {InnerContent}
          </Col>
        </Row>
      </div>
    </Content>
  );
};

export default Cta;
